import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetTitle from '@/libs/antd/components/Title';
import {
	useCancelPO,
	useGetPurchaseOrderDetail,
	useGetPurchaseOrderDetailSearchOptions,
	usePrintPurchaseOrder,
} from '@/libs/fleet-api/hooks/purchase-order';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
	Button,
	DatePicker,
	Input,
	Modal,
	Select,
	Space,
	Table,
	notification,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';

const pageSize = 10;

const FRMPODetailListPage: FC<{
	mppId: number;
	mklNo: string;
	spfNo: string;
	purchaseOrderNo?: string;
	onBack?: () => void;
	isCancelEnabled?: boolean;
}> = ({ mppId, mklNo, spfNo, purchaseOrderNo, onBack, isCancelEnabled }) => {
	const { navigate } = useApp();
	const queryClient = useQueryClient();
	const { mutateAsync: cancelPOMutate, isPending: isCancelPending } =
		useCancelPO();

	const { openCustomerDetail, openCustomerGroupDetail, openDocumentModal } =
		useModals();

	const { mutateAsync: printPurchaseOrderMutate } = usePrintPurchaseOrder();

	const { data: searchOptions } = useGetPurchaseOrderDetailSearchOptions();

	const [searchBy, setSearchBy] = useState<string>('');
	const [searchValue, setSearchValue] = useState<string>('');
	const [page, setPage] = useState(1);
	const [triggeredSearch, setTriggeredSearch] = useState<{
		by: string;
		value: string;
	} | null>(
		purchaseOrderNo ? { by: 'purchaseOrderNo', value: purchaseOrderNo } : null,
	);

	const [statusOptions, setStatusOptions] = useState<
		{ id: string; value: string }[]
	>([]);
	const [searchByOptions, setSearchByOptions] = useState<
		{ id: string; value: string }[]
	>([]);
	const [supplierOptions, setSupplierOptions] = useState<
		{ id: string; value: string }[]
	>([]);
	const [brandsOptions, setBrandsOptions] = useState<
		{ id: string; value: string }[]
	>([]);

	// Prepare params for API call
	const params: {
		pagination: { pageSize: number; pageNumber: number };
		mppId?: number;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		[key: string]: any;
	} = {
		pagination: { pageSize, pageNumber: page },
		mppId,
	};

	if (triggeredSearch && triggeredSearch.value) {
		if (
			triggeredSearch.by === 'poReleaseDate' ||
			triggeredSearch.by === 'poExpiredDate' ||
			triggeredSearch.by === 'poExtendDate'
		) {
			params[triggeredSearch.by] = dayjs(triggeredSearch.value)
				.add(1, 'day') // timezine fix
				.toISOString();
		} else if (['poStatus'].includes(triggeredSearch.by)) {
			params[triggeredSearch.by] = triggeredSearch.value;
		} else {
			params.search = {
				by: triggeredSearch.by,
				value: triggeredSearch.value,
			};
		}
	}

	const { data, isLoading, error } = useGetPurchaseOrderDetail(params);

	// Update options from API response
	useEffect(() => {
		if (searchOptions) {
			setStatusOptions(searchOptions.statusOptions || []);
			setSupplierOptions(searchOptions.supplierOptions || []);
			setBrandsOptions(searchOptions.brandsOptions || []);

			const staticSearchOptions = [
				{ id: 'poReleaseDate', value: 'Tanggal Terbit PO' },
				{ id: 'poExpiredDate', value: 'Tanggal PO Expired' },
			];

			const combinedSearchOptions = [
				...(searchOptions.searchByOptions || []),
				...staticSearchOptions,
			];

			setSearchByOptions(combinedSearchOptions);
			if (combinedSearchOptions.length > 0 && !searchBy) {
				setSearchBy(combinedSearchOptions[0].id);
			}
		}
	}, [searchOptions, searchBy]);

	// Reset page to 1 on new search
	useEffect(() => {
		if (triggeredSearch) setPage(1);
	}, [triggeredSearch]);

	const handleSearch = () => {
		setTriggeredSearch({ by: searchBy, value: searchValue });
		setPage(1);
	};

	const handleCancelPO = (purchaseOrderId: number) => {
		Modal.confirm({
			title: 'Konfirmasi Pembatalan PO',
			content: 'Apakah Anda yakin untuk membatalkan PO ini?',
			okText: 'Ya',
			cancelText: 'Tidak',
			okButtonProps: { danger: true },
			onOk: async () => {
				await cancelPOMutate(String(purchaseOrderId));
				notification.success({
					message: 'Berhasil',
					description: 'PO berhasil dibatalkan.',
				});
				queryClient.invalidateQueries({ queryKey: ['getPurchaseOrderDetail'] });
			},
		});
	};

	// Table data
	const tableData = (data?.details || []).map((item, idx) => ({
		key: idx + 1 + (page - 1) * pageSize,
		no: idx + 1 + (page - 1) * pageSize,
		noMkl: mklNo,
		noSpf: spfNo,
		mppId: data?.mppId,
		spfId: item?.spfId,
		napResolutionId: item?.napResolutionId,
		noMpp: data?.mppNo,
		noPo: item.purchaseOrderNo,
		purchaseOrderId: item.purchaseOrderId,
		applicationId: item.applicationId || '-',
		noAgreement: item.agreementNo || '-',
		supplier: item.supplierName,
		merkAsset: item.brand,
		ntf: item.ntfAmount,
		tglTerbitPo: item.purchaseOrderReleaseDate,
		tglPoExpired: item.purchaseOrderExpiredDate,
		tglPerpanjanganPo: item.purchaseOrderExtendDate || '-',
		status: item.status,
	}));

	// Dynamic input for search value
	let searchValueInput = null;
	if (searchBy === 'poReleaseDate' || searchBy === 'poExpiredDate') {
		searchValueInput = (
			<DatePicker
				value={searchValue ? dayjs(searchValue) : null}
				onChange={(date: dayjs.Dayjs | null) => {
					setSearchValue(date ? date.toISOString() : '');
				}}
				style={{ borderRadius: 8, width: '40%' }}
				format={{ format: 'DD-MM-YYYY', type: 'mask' }}
			/>
		);
	} else if (searchBy === 'poStatus') {
		searchValueInput = (
			<Select
				style={{ minWidth: 160, width: '40%' }}
				value={searchValue}
				onChange={setSearchValue}
				options={statusOptions.map((opt) => ({
					label: opt.value,
					value: opt.id,
				}))}
				allowClear
				placeholder="Pilih status..."
			/>
		);
	} else if (searchBy === 'supplier') {
		searchValueInput = (
			<Select
				style={{ minWidth: 160, width: '40%' }}
				value={searchValue}
				onChange={setSearchValue}
				options={supplierOptions.map((opt) => ({
					label: opt.value,
					value: opt.id,
				}))}
				allowClear
				placeholder="Pilih supplier..."
			/>
		);
	} else if (searchBy === 'brand') {
		searchValueInput = (
			<Select
				style={{ minWidth: 160, width: '40%' }}
				value={searchValue}
				onChange={setSearchValue}
				options={brandsOptions.map((opt) => ({
					label: opt.value,
					value: opt.id,
				}))}
				allowClear
				placeholder="Pilih merk asset..."
			/>
		);
	} else {
		searchValueInput = (
			<Input
				placeholder="Masukkan kata kunci..."
				value={searchValue}
				onChange={(e) => setSearchValue(e.target.value)}
				onPressEnter={handleSearch}
				style={{ borderRadius: 8, width: '40%' }}
				allowClear
			/>
		);
	}

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => {
							if (onBack) {
								onBack();
							} else {
								navigate('FRMPOListPage');
							}
						}}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>Detail Purchase Order</FleetTitle>
				</div>
			</div>
			<DataRow
				items={[
					[
						[
							'Nama Customer',
							data?.customerName ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(data!.customerId);
									}}
								>
									{data?.customerName}
								</a>
							) : (
								'-'
							),
						],
						[
							'Group Customer BMPP',
							data?.bmppGroupId ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(data!.bmppGroupId);
									}}
								>
									{data?.bmppGroupName}
								</a>
							) : (
								'-'
							),
						],
					],
				]}
				loading={isLoading}
			/>
			<div style={{ display: 'flex', gap: 8, width: '100%' }}>
				<Select
					style={{ minWidth: 160, width: '40%' }}
					value={searchBy}
					onChange={(value) => {
						setSearchBy(value);
						setSearchValue('');
					}}
					options={searchByOptions.map((opt) => ({
						label: opt.value,
						value: opt.id,
					}))}
					placeholder="Cari berdasarkan"
				/>
				{searchValueInput}
				<Button
					type="primary"
					onClick={handleSearch}
					style={{ width: 190 }}
				>
					SEARCH
				</Button>
			</div>
			<FleetTitle>Daftar PO</FleetTitle>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={tableData}
				columns={[
					{ title: 'No', dataIndex: 'no', align: 'center', width: 1 },
					{ title: 'No. MKL', dataIndex: 'noMkl' },
					{
						title: 'No.SPF',
						dataIndex: 'noSpf',
						render: (v, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									// TODO: change to actual prospect id
									navigate('SPFPage', {
										spfId: record.spfId,
										napResolutionId: record.napResolutionId,
										onBack: () =>
											navigate('FRMPODetailListPage', {
												mppId: mppId,
												mklNo: mklNo,
												spfNo: spfNo,
											}),
										viewOnly: true,
									});
								}}
							>
								{v}
							</a>
						),
					},
					{
						title: 'No. MPP',
						dataIndex: 'noMpp',
						render: (v, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									if (record.mppId)
										navigate('MPPPage', {
											mppId: record.mppId,
											viewOnly: true,
											onBack: () =>
												navigate('FRMPODetailListPage', {
													mppId: mppId,
													mklNo: mklNo,
													spfNo: spfNo,
												}),
										});
								}}
							>
								{v}
							</a>
						),
					},
					{
						title: 'No. PO',
						dataIndex: 'noPo',
						render: (v, record) => (
							<a
								onClick={async (e) => {
									e.preventDefault();
									if (v && record.purchaseOrderId) {
										try {
											const response = await printPurchaseOrderMutate({
												purchaseOrderIds: [record.purchaseOrderId],
											});
											openDocumentModal(response);
										} catch (error) {
											console.error('Failed to load PO document:', error);
										}
									}
								}}
								style={{ cursor: v ? 'pointer' : 'default' }}
							>
								{v || '-'}
							</a>
						),
					},
					{ title: 'Application ID', dataIndex: 'applicationId' },
					{ title: 'No Agreement', dataIndex: 'noAgreement' },
					{ title: 'Supplier', dataIndex: 'supplier' },
					{ title: 'Merk Asset', dataIndex: 'merkAsset' },
					{ title: 'NTF', dataIndex: 'ntf' },
					{
						title: 'Tanggal Terbit PO',
						dataIndex: 'tglTerbitPo',
						render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '-'),
					},
					{
						title: 'Tanggal PO Expired',
						dataIndex: 'tglPoExpired',
						render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '-'),
					},
					{ title: 'Tanggal Perpanjangan PO', dataIndex: 'tglPerpanjanganPo' },
					{ title: 'Status', dataIndex: 'status' },
					...(isCancelEnabled
						? [
								{
									title: 'Aksi',
									key: 'aksi',
									render: (_: unknown, record: { purchaseOrderId: number }) => (
										<Button
											danger
											size="small"
											disabled={isCancelPending}
											onClick={() => handleCancelPO(record.purchaseOrderId)}
										>
											Cancel PO
										</Button>
									),
								},
							]
						: []),
				]}
				pagination={{
					current: page,
					total: data?.details.length || 0,
					pageSize,
					onChange: setPage,
					showSizeChanger: false,
				}}
				rowKey="key"
			/>
			{error && (
				<div style={{ color: 'red' }}>Gagal memuat detail purchase order.</div>
			)}
		</Space>
	);
};

export default FRMPODetailListPage;
